import { Location, NgClass, NgOptimizedImage } from '@angular/common'
import type { ElementRef, Signal, WritableSignal } from '@angular/core'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core'
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { addMonths, addYears, isAfter, startOfDay } from 'date-fns'
import { NgArrayPipesModule } from 'ngx-pipes'
import {
  EMPTY,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  switchMap,
  tap,
} from 'rxjs'

import type { ProductDto, SubscriptionDto } from '../../../api/responses'
import type { BillingCycleDto } from '../../../api/responses/organization'
import { OrganizationService } from '../../../services/organization/organization.service'
import { ProductsService } from '../../../services/product/products.service'
import { SubscriptionService } from '../../../services/subscription/subscription.service'
import { LocalizedDatePipe } from '../../../shared/pipes/localized-date.pipe'
import { withLocalePrefix } from '../../../shared/providers/locale-aware-router'
import { OrganizationContactInfoComponent } from '../components/organization-contact-info/organization-contact-info.component'
import { ProductCardComponent } from '../components/product-card/product-card.component'
import { ProductCheckoutListComponent } from '../components/product-checkout-list/product-checkout-list.component'
import type { OrderLine } from '../models/order-line'

type ConfigureSubscriptionInput = Parameters<
  SubscriptionService['configureSubscription']
>[0]

export enum subscriptionStartDateOption {
  Now = 'startNow',
  StartNewSubscriptionPeriod = 'startNewSubscriptionPeriod',
}

@Component({
  selector: 'dhp-product-basket',
  templateUrl: './products-checkout.component.html',
  styleUrl: './products-checkout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProductCardComponent,
    NgClass,
    NgOptimizedImage,
    ProductCheckoutListComponent,
    TranslatePipe,
    NgArrayPipesModule,
    OrganizationContactInfoComponent,
    FormsModule,
    LocalizedDatePipe,
  ],
})
export class ProductsCheckoutComponent {
  private readonly productsService = inject(ProductsService)
  private readonly subscriptionService = inject(SubscriptionService)
  private readonly organizationService = inject(OrganizationService)
  private readonly location = inject(Location)
  private readonly router = inject(Router)
  private readonly destroyRef = inject(DestroyRef)

  // Next invoicing date
  private nextInvoicingDate: Date | null = null
  // Flag to ensure order lines are initialized only once
  private orderLinesInitialized = false

  // Interpolated into translated HTML rendered via [innerHTML], which can't
  // go through routerLink/the Router - so the /en prefix has to be applied
  // here explicitly instead of relying on LocaleAwareRouter.
  get termsRoute(): string {
    return withLocalePrefix(this.router.url, '/terms-and-conditions')
  }

  products = this.getAllProducts()
  contentIntegrators = this.getAllContentIntegrators()
  orderLines = signal<OrderLine[] | undefined>(undefined)
  isCreatingSubscriptions = signal<boolean>(false)
  isError = signal<boolean>(false)
  hasAcceptedTermsConditions = signal<boolean>(false)
  organization = toSignal(this.organizationService.getOrganization())
  // Active subscription end date
  activeSubscriptionEndDate = signal<Date | null>(null)
  activeSubscriptionStartDate = signal<Date | null>(null)
  readonly requiresNextPeriodOrder = signal(false)
  // reference to an existing subscription
  billingCycleReference: WritableSignal<string | null> = signal(null)

  // Valid subscription start dates
  public readonly validDates: Date[] = [new Date()]
  // Subscription start date options
  public readonly subscriptionStartDateOption = subscriptionStartDateOption

  // Selected subscription start date option
  public selectedSubscriptionStartDateOption: subscriptionStartDateOption =
    subscriptionStartDateOption.Now
  private readonly selectedSubscriptionStartDateOptionState = signal(
    subscriptionStartDateOption.Now
  )

  // Initialize with 'Now' so proration is positive on first render
  public subscriptionStartDate: Date | null = new Date()
  public subscriptionPeriodStartDate: Date | null = new Date()
  // The default subscription end date is one year from the start date
  public subscriptionPeriodEndDate: Date | null = addYears(new Date(), 1)

  protected selectedOrganizationId = signal<number | null>(null)
  protected selectedOrganizationName = signal<string | null>(null)

  private readonly subscriptions = toSignal(
    toObservable(this.selectedOrganizationId).pipe(
      distinctUntilChanged(),
      switchMap(organizationId =>
        this.subscriptionService.getSubscriptions(organizationId ?? undefined)
      )
    ),
    { initialValue: [] }
  )

  private readonly subscribedProductIds = computed(
    () =>
      new Set(this.subscriptions().map(subscription => subscription.product.id))
  )

  private readonly productTypesById = computed(
    () =>
      new Map(
        (this.products() ?? []).map(product => [
          product.id,
          product.productType,
        ])
      )
  )

  readonly hasSelectedContentIntegrator = computed(() =>
    this.getPositiveOrderLines().some(orderLine =>
      this.isContentIntegratorProduct(orderLine.product)
    )
  )

  readonly hasExistingContentIntegrator = computed(() =>
    this.getRelevantSubscriptionStates().some(
      subscriptionState =>
        subscriptionState.productType === 'content_integrator' &&
        subscriptionState.seats > 0
    )
  )

  readonly requiresMinimumSeats = computed(
    () =>
      this.hasSelectedContentIntegrator() || this.hasExistingContentIntegrator()
  )

  readonly checkoutValidationKeys = computed(() => {
    const validationKeys: string[] = []

    if (this.hasOrderLinesBelowMinimumSeats()) {
      validationKeys.push(
        'pages.checkout.validation.minimumSeatsWithContentIntegrator'
      )
    }

    return validationKeys
  })

  readonly requiredUpgradeOrderLines = computed(() => {
    if (!this.requiresMinimumSeats()) {
      return [] as OrderLine[]
    }

    const productsById = new Map(
      (this.products() ?? []).map(product => [product.id, product])
    )

    return this.subscriptions().flatMap(subscription => {
      const product = productsById.get(subscription.product.id)
      if (
        !product ||
        !this.isMinimumSeatRestrictedProduct(product.productType)
      ) {
        return []
      }

      const relevantSeats = this.getRelevantSeatsForUpgrade(subscription)

      if (relevantSeats <= 0 || relevantSeats >= 3) {
        return []
      }

      const autoIncludedUpgradeType =
        this.getAutoIncludedUpgradeType(subscription)
      if (!autoIncludedUpgradeType) {
        return []
      }

      return [
        {
          product,
          seats: 3,
          currentSeats:
            autoIncludedUpgradeType === 'increaseCurrentSeats'
              ? relevantSeats
              : undefined,
          existingSeats: subscription.totalSeats,
          scheduledSeats: this.getScheduledSeats(subscription) ?? undefined,
          sourceSubscriptionId: subscription.id,
          isAutoIncludedUpgrade: true,
          autoIncludedUpgradeType,
        },
      ]
    })
  })

  readonly orderedOrderLines = computed(() =>
    [...(this.orderLines() ?? [])].sort((left, right) => {
      const leftIsContentIntegrator = this.isContentIntegratorProduct(
        left.product
      )
      const rightIsContentIntegrator = this.isContentIntegratorProduct(
        right.product
      )

      if (leftIsContentIntegrator !== rightIsContentIntegrator) {
        return leftIsContentIntegrator ? 1 : -1
      }

      return left.product.name.localeCompare(right.product.name)
    })
  )

  readonly checkoutOrderLines = computed(() => [
    ...this.getPositiveOrderLines(),
    ...this.requiredUpgradeOrderLines(),
  ])

  readonly summaryOrderLines = computed(() =>
    this.checkoutOrderLines().filter(
      orderLine => !this.isScheduledOnlyUpgradeOrderLine(orderLine)
    )
  )

  readonly hasAutoIncludedUpgrades = computed(
    () => this.requiredUpgradeOrderLines().length > 0
  )

  readonly hasScheduledOnlyUpgradeOrderLines = computed(
    () => this.getScheduledOnlyUpgradeOrderLines().length > 0
  )

  getAutoIncludedUpgradeDescriptionKey(orderLine: OrderLine): string {
    switch (orderLine.autoIncludedUpgradeType) {
      case 'adjustScheduledDecrease':
        return 'pages.checkout.info.autoIncludedUpgradeAdjustScheduled'
      case 'removeScheduledDecrease':
        return 'pages.checkout.info.autoIncludedUpgradeRemoveScheduled'
      case 'increaseCurrentSeats':
      default:
        return 'pages.checkout.info.autoIncludedUpgradeIncrease'
    }
  }

  getAutoIncludedUpgradeDescriptionParams(orderLine: OrderLine): {
    current?: number
    from?: number
    to: number
  } {
    switch (orderLine.autoIncludedUpgradeType) {
      case 'adjustScheduledDecrease':
        return {
          current: orderLine.existingSeats,
          from: orderLine.scheduledSeats,
          to: orderLine.seats,
        }
      case 'removeScheduledDecrease':
        return {
          current: orderLine.existingSeats,
          from: orderLine.scheduledSeats,
          to: orderLine.seats,
        }
      case 'increaseCurrentSeats':
      default:
        return {
          from: orderLine.currentSeats,
          to: orderLine.seats,
        }
    }
  }

  getAutoIncludedUpgradeBadgeKey(orderLine: OrderLine): string {
    switch (orderLine.autoIncludedUpgradeType) {
      case 'adjustScheduledDecrease':
        return 'pages.checkout.info.autoIncludedUpgradeBadgeAdjusted'
      case 'removeScheduledDecrease':
        return 'pages.checkout.info.autoIncludedUpgradeBadgeRemoved'
      case 'increaseCurrentSeats':
      default:
        return 'pages.checkout.info.autoIncludedUpgradeBadgeAdded'
    }
  }

  getAutoIncludedUpgradeBadgeClasses(orderLine: OrderLine): string {
    switch (orderLine.autoIncludedUpgradeType) {
      case 'adjustScheduledDecrease':
        return 'bg-amber-100 text-amber-700'
      case 'removeScheduledDecrease':
        return 'bg-emerald-100 text-emerald-700'
      case 'increaseCurrentSeats':
      default:
        return 'bg-sky-100 text-sky-700'
    }
  }

  readonly isCheckoutDisabled = computed(
    () =>
      this.checkoutOrderLines().length === 0 ||
      this.checkoutValidationKeys().length > 0
  )

  // Confirmation dialog reference
  readonly confirmDialog =
    viewChild<ElementRef<HTMLDialogElement>>('confirmDialog')

  constructor() {
    // Initialize order lines from navigation state
    this.initOrderLines()
    // Get subscription reference and next invoicing date
    this.getSubscriptionReferenceAndDate()

    effect(() => {
      if (!this.requiresMinimumSeats()) {
        return
      }

      const orderLines = this.orderLines()
      if (!orderLines) {
        return
      }

      const normalizedOrderLines = orderLines.map(orderLine => {
        if (
          this.isMinimumSeatRestrictedProduct(orderLine.product.productType) &&
          orderLine.seats > 0 &&
          orderLine.seats < 3
        ) {
          return { ...orderLine, seats: 3 }
        }

        return orderLine
      })

      const hasChanges = normalizedOrderLines.some(
        (orderLine, index) => orderLine.seats !== orderLines[index]?.seats
      )

      if (hasChanges) {
        this.orderLines.set(normalizedOrderLines)
      }
    })
  }

  // Open confirmation dialog
  openConfirmation(): void {
    this.confirmDialog()?.nativeElement.showModal()
  }

  // Add order line
  addOrderLine(product: ProductDto, seats: number): void {
    this.orderLines.update(ol => [...(ol ?? []), { product, seats }])
  }

  onOrderLineSeatsChange(productId: number, seats: number): void {
    this.orderLines.update(orderLines => {
      if (!orderLines) {
        return orderLines
      }

      return orderLines
        .map(orderLine =>
          orderLine.product.id === productId
            ? { ...orderLine, seats }
            : orderLine
        )
        .filter(
          orderLine =>
            !(
              this.isContentIntegratorProduct(orderLine.product) &&
              orderLine.seats <= 0
            )
        )
    })
  }

  onOrderLineCustomerCostReferenceChange(
    productId: number,
    customerCostReference: string | undefined
  ): void {
    this.orderLines.update(orderLines => {
      if (!orderLines) {
        return orderLines
      }

      return orderLines.map(orderLine =>
        orderLine.product.id === productId
          ? { ...orderLine, customerCostReference }
          : orderLine
      )
    })
  }

  // Remove order line
  getAllContentIntegrators(): Signal<ProductDto[] | undefined> {
    return computed(() => {
      const selectedContentIntegratorIds = this.orderLines()?.map(
        ol => ol.product.id
      )
      return this.products()?.filter(
        p =>
          p.productType === 'content_integrator' &&
          !selectedContentIntegratorIds?.includes(p.id) &&
          !this.subscribedProductIds().has(p.id)
      )
    })
  }

  // Close confirmation dialog
  checkout(): void {
    this.createSubscriptions()
  }

  // Toggle acceptance of terms and conditions
  toggleTermsAndConditions(): void {
    this.hasAcceptedTermsConditions.update(v => !v)
  }

  // Add content integrator with seats equal to total seats of other products
  addContentIntegrator(ci: ProductDto): void {
    if (ci.product_options.docs.length === 0) {
      return
    }

    this.addOrderLine(ci, 3)
  }

  getMinSelected(product: ProductDto): number | undefined {
    if (product.productType === 'content_integrator') {
      return 3
    }

    if (
      this.requiresMinimumSeats() &&
      this.isMinimumSeatRestrictedProduct(product.productType)
    ) {
      return 3
    }

    return undefined
  }

  get isOrderingAsReseller(): boolean {
    return (
      this.selectedOrganizationId() !== null &&
      this.selectedOrganizationId() !== this.organization()?.id
    )
  }

  // Set subscription dates based on selected date
  protected setSubscriptionDates(option: subscriptionStartDateOption): void {
    if (
      option === subscriptionStartDateOption.Now &&
      this.requiresNextPeriodOrder()
    ) {
      option = subscriptionStartDateOption.StartNewSubscriptionPeriod
    }

    // Update selected option
    this.selectedSubscriptionStartDateOption = option
    this.selectedSubscriptionStartDateOptionState.set(option)
    // Get today's date
    const today = new Date()

    // Set subscription dates based on selected option
    switch (option) {
      case subscriptionStartDateOption.Now:
        // Set start date to today
        this.subscriptionStartDate = today
        this.subscriptionPeriodStartDate = today
        this.subscriptionPeriodEndDate =
          this.activeSubscriptionEndDate() ?? addYears(today, 1)
        break
      case subscriptionStartDateOption.StartNewSubscriptionPeriod:
        // Set start date to end date of active subscription and default to today if none
        this.subscriptionStartDate = this.activeSubscriptionEndDate()
        this.subscriptionStartDate ??= today
        this.subscriptionPeriodStartDate = this.subscriptionStartDate

        // Set end date to January 1st of the year after next
        this.subscriptionPeriodEndDate = addYears(this.subscriptionStartDate, 1)
        break
    }
  }

  // Get all order lines from navigation state
  private initOrderLines(): void {
    // Get products from navigation state
    const { products, organizationId, organizationName } =
      this.location.getState() as {
        products: [number, number][]
        organizationId: number | null
        organizationName: string | null
      }
    this.selectedOrganizationId.set(organizationId)
    this.selectedOrganizationName.set(organizationName ?? null)
    // Create a map of selected products
    const selected = new Map(products)

    // Initialize order lines when products are available
    effect(() => {
      // Get all products
      const all = this.products()
      if (!all || this.orderLinesInitialized) return

      // Filter and map selected products to order lines
      const lines = all
        .filter(p => selected.has(p.id))
        .map(p => ({ product: p, seats: selected.get(p.id) ?? 0 }))

      // Set order lines signal
      this.orderLines.set(lines)
      // Mark order lines as initialized
      this.orderLinesInitialized = true
    })
  }

  // Get all products
  private getAllProducts(): Signal<ProductDto[] | undefined> {
    return toSignal(this.productsService.getProducts())
  }

  // Get subscription reference and billing period dates
  private getSubscriptionReferenceAndDate(): void {
    toObservable(this.selectedOrganizationId)
      .pipe(
        distinctUntilChanged(),
        switchMap(organizationId =>
          combineLatest([
            this.subscriptionService.getSubscriptions(
              organizationId ?? undefined
            ),
            this.organizationService.getBillingCycles(organizationId),
          ])
        ),
        tap(([subscriptions, billingCycles]) => {
          const relevantBillingCycle =
            this.getRelevantBillingCycle(billingCycles)

          // Set billing cycle reference if available
          if (relevantBillingCycle?.reference) {
            this.billingCycleReference.set(relevantBillingCycle.reference)
          }
          if (relevantBillingCycle?.nextInvoicingDate) {
            this.applyBillingTimeline(
              new Date(relevantBillingCycle.nextInvoicingDate),
              new Date(relevantBillingCycle.startDate)
            )

            return
          }

          this.applyBillingTimeline(
            this.getNextInvoicingDateFromSubscriptions(subscriptions)
          )
        }),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()
  }

  private getRelevantBillingCycle(
    billingCycles: BillingCycleDto[]
  ): BillingCycleDto | null {
    const annualBillingCycle = billingCycles.find(
      billingCycle => billingCycle.frequency === 'ANNUALLY'
    )

    if (annualBillingCycle) {
      return annualBillingCycle
    }

    return (
      billingCycles.find(billingCycle => !!billingCycle.nextInvoicingDate) ??
      null
    )
  }

  private getNextInvoicingDateFromSubscriptions(
    subscriptions: SubscriptionDto[]
  ): Date | null {
    const invoiceSub = [...subscriptions]
      .filter(sub => !!sub.billingCycle.nextInvoicingDate)
      .sort(
        (a, b) =>
          new Date(b.billingCycle.nextInvoicingDate ?? 0).getTime() -
          new Date(a.billingCycle.nextInvoicingDate ?? 0).getTime()
      )
      .at(0)

    return invoiceSub?.billingCycle.nextInvoicingDate
      ? new Date(invoiceSub.billingCycle.nextInvoicingDate)
      : null
  }

  private applyBillingTimeline(
    nextInvoicingDate: Date | null,
    startDate: Date | null = null
  ): void {
    const requiresNextPeriodOrder = this.isFutureBillingCycleStart(startDate)

    this.requiresNextPeriodOrder.set(requiresNextPeriodOrder)
    this.activeSubscriptionStartDate.set(startDate)
    this.nextInvoicingDate = nextInvoicingDate
    this.activeSubscriptionEndDate.set(nextInvoicingDate)
    this.resetValidDates()
    this.setSubscriptionDates(
      requiresNextPeriodOrder
        ? subscriptionStartDateOption.StartNewSubscriptionPeriod
        : this.selectedSubscriptionStartDateOption
    )

    if (requiresNextPeriodOrder) {
      return
    }

    if (
      !nextInvoicingDate ||
      !this.canStartAtNextBillingPeriod(nextInvoicingDate)
    ) {
      return
    }

    this.validDates.push(nextInvoicingDate)
    this.setSubscriptionDates(this.selectedSubscriptionStartDateOption)
  }

  private resetValidDates(): void {
    this.validDates.length = 1
    this.validDates[0] = new Date()
  }

  private canStartAtNextBillingPeriod(nextInvoicingDate: Date): boolean {
    return isAfter(
      startOfDay(new Date()),
      startOfDay(addMonths(nextInvoicingDate, -2))
    )
  }

  private isFutureBillingCycleStart(startDate: Date | null): boolean {
    if (startDate === null) {
      return false
    }

    return startOfDay(startDate).getTime() > startOfDay(new Date()).getTime()
  }

  // Create subscriptions for all order lines
  private createSubscriptions(): void {
    // Reset error state
    this.isError.set(false)

    // Prevent multiple subscription creation processes
    if (this.isCreatingSubscriptions()) {
      return
    }

    const configuration = this.buildSubscriptionConfiguration()
    if (!configuration) {
      return
    }

    // Set creating subscription flag
    this.isCreatingSubscriptions.set(true)

    this.subscriptionService
      .configureSubscription(configuration)
      .pipe(
        // Navigate to payment page on success
        tap(paymentUrl => {
          if (paymentUrl === null) {
            this.location.back()

            return
          }

          const subscriptionStartDate = this.subscriptionStartDate

          if (subscriptionStartDate === null)
            throw Error('Subscription start date is null')

          const startDate: Date = startOfDay(subscriptionStartDate)

          void this.router.navigate(['/products/payment'], {
            state: {
              startDate,
              paymentUrl,
            },
          })
        }),
        catchError((error: unknown) => {
          console.error(error)
          this.isError.set(true)
          return EMPTY
        }),
        finalize(() => {
          this.isCreatingSubscriptions.set(false)
        })
      )
      .subscribe()
  }

  private buildSubscriptionConfiguration(): ConfigureSubscriptionInput | null {
    const checkoutOrderLines = this.checkoutOrderLines().filter(
      orderLine => orderLine.seats > 0
    )

    if (checkoutOrderLines.length === 0) {
      return null
    }

    const globalReference = this.normalizeReference(
      this.billingCycleReference()
    )
    const applyImmediately = this.shouldApplyChangesImmediately()

    const productsToAdd = checkoutOrderLines
      .filter(orderLine => typeof orderLine.sourceSubscriptionId !== 'number')
      .map(orderLine => ({
        id: orderLine.product.id,
        quantity: orderLine.seats,
        reference: this.getReferenceForOrderLine(orderLine),
        addImmediately: applyImmediately,
      }))

    const subscriptionsToUpdate = checkoutOrderLines
      .filter(
        (
          orderLine
        ): orderLine is OrderLine & { sourceSubscriptionId: number } =>
          typeof orderLine.sourceSubscriptionId === 'number'
      )
      .map(orderLine => ({
        id: orderLine.sourceSubscriptionId,
        quantity: orderLine.seats,
        reference: this.getReferenceForOrderLine(orderLine),
        updateImmediately: this.shouldUpdateOrderLineImmediately(orderLine),
      }))

    return {
      organizationId: this.selectedOrganizationId(),
      reference: globalReference,
      productsToAdd: productsToAdd.length > 0 ? productsToAdd : null,
      subscriptionsToUpdate:
        subscriptionsToUpdate.length > 0 ? subscriptionsToUpdate : null,
    }
  }

  private shouldApplyChangesImmediately(): boolean {
    return (
      this.selectedSubscriptionStartDateOptionState() ===
      subscriptionStartDateOption.Now
    )
  }

  private shouldUpdateOrderLineImmediately(orderLine: OrderLine): boolean {
    if (this.isScheduledOnlyUpgradeOrderLine(orderLine)) {
      return false
    }

    return this.shouldApplyChangesImmediately()
  }

  private getReferenceForOrderLine(orderLine: OrderLine): string | null {
    return this.normalizeReference(orderLine.customerCostReference)
  }

  private normalizeReference(
    reference: string | null | undefined
  ): string | null {
    const trimmedReference = reference?.trim()

    if (!trimmedReference) {
      return null
    }

    return trimmedReference
  }

  private getPositiveOrderLines(): OrderLine[] {
    return this.orderLines()?.filter(orderLine => orderLine.seats > 0) ?? []
  }

  private isContentIntegratorProduct(product: Pick<ProductDto, 'id'>): boolean {
    return this.productTypesById().get(product.id) === 'content_integrator'
  }

  private isMinimumSeatRestrictedProduct(
    productType: ProductDto['productType'] | undefined
  ): boolean {
    return productType === 'magazine' || productType === 'package'
  }

  private getRelevantSubscriptionStates(): {
    productId: number
    productType: ProductDto['productType'] | undefined
    seats: number
  }[] {
    return this.subscriptions().flatMap(subscription => {
      const productType = this.productTypesById().get(subscription.product.id)
      const currentState = {
        productId: subscription.product.id,
        productType,
        seats: subscription.totalSeats,
      }
      const scheduledSeats = this.getScheduledSeats(subscription)

      if (
        this.selectedSubscriptionStartDateOptionState() ===
        subscriptionStartDateOption.StartNewSubscriptionPeriod
      ) {
        return [
          {
            ...currentState,
            seats: scheduledSeats ?? subscription.totalSeats,
          },
        ]
      }

      return scheduledSeats === null
        ? [currentState]
        : [currentState, { ...currentState, seats: scheduledSeats }]
    })
  }

  private getScheduledSeats(subscription: SubscriptionDto): null | number {
    const scheduledChange = subscription.scheduledChange?.docs[0]
    if (!scheduledChange) {
      return null
    }

    return scheduledChange.product_options.reduce((total, productOption) => {
      const seatValue = productOption.product_option.seat_value

      return total + productOption.quantity * (seatValue ?? 1)
    }, 0)
  }

  private getRelevantSeatsForUpgrade(subscription: SubscriptionDto): number {
    const currentSeats = subscription.totalSeats
    const scheduledSeats = this.getScheduledSeats(subscription)
    const nextSeats = scheduledSeats ?? currentSeats
    const contentIntegratorActiveNow =
      this.hasContentIntegratorInCurrentPeriod()
    const contentIntegratorActiveNext = this.hasContentIntegratorInNextPeriod()

    if (
      this.selectedSubscriptionStartDateOptionState() ===
      subscriptionStartDateOption.StartNewSubscriptionPeriod
    ) {
      return contentIntegratorActiveNext ? nextSeats : 0
    }

    if (contentIntegratorActiveNow && currentSeats > 0 && currentSeats < 3) {
      return currentSeats
    }

    if (contentIntegratorActiveNext && nextSeats > 0 && nextSeats < 3) {
      return nextSeats
    }

    return 0
  }

  private getScheduledOnlyUpgradeOrderLines(): OrderLine[] {
    return this.requiredUpgradeOrderLines().filter(orderLine =>
      this.isScheduledOnlyUpgradeOrderLine(orderLine)
    )
  }

  private isScheduledOnlyUpgradeOrderLine(orderLine: OrderLine): boolean {
    return (
      orderLine.autoIncludedUpgradeType === 'adjustScheduledDecrease' ||
      orderLine.autoIncludedUpgradeType === 'removeScheduledDecrease'
    )
  }

  private getAutoIncludedUpgradeType(
    subscription: SubscriptionDto
  ): OrderLine['autoIncludedUpgradeType'] | undefined {
    const currentSeats = subscription.totalSeats
    const scheduledSeats = this.getScheduledSeats(subscription)

    if (
      this.hasContentIntegratorInCurrentPeriod() &&
      currentSeats > 0 &&
      currentSeats < 3
    ) {
      return 'increaseCurrentSeats'
    }

    if (
      this.hasContentIntegratorInNextPeriod() &&
      scheduledSeats !== null &&
      scheduledSeats > 0 &&
      scheduledSeats < 3
    ) {
      if (currentSeats <= 3) {
        return 'removeScheduledDecrease'
      }

      return 'adjustScheduledDecrease'
    }

    return undefined
  }

  private hasContentIntegratorInCurrentPeriod(): boolean {
    return (
      this.subscriptions().some(
        subscription =>
          this.productTypesById().get(subscription.product.id) ===
            'content_integrator' && subscription.totalSeats > 0
      ) ||
      (this.selectedSubscriptionStartDateOptionState() ===
        subscriptionStartDateOption.Now &&
        this.hasSelectedContentIntegrator())
    )
  }

  private hasContentIntegratorInNextPeriod(): boolean {
    return (
      this.subscriptions().some(subscription => {
        if (
          this.productTypesById().get(subscription.product.id) !==
          'content_integrator'
        ) {
          return false
        }

        return (
          (this.getScheduledSeats(subscription) ?? subscription.totalSeats) > 0
        )
      }) || this.hasSelectedContentIntegrator()
    )
  }

  private hasOrderLinesBelowMinimumSeats(): boolean {
    if (!this.requiresMinimumSeats()) {
      return false
    }

    return this.getPositiveOrderLines().some(
      orderLine =>
        this.isMinimumSeatRestrictedProduct(orderLine.product.productType) &&
        orderLine.seats < 3
    )
  }
}
